import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/api";
import { pick, useLang } from "@/i18n";
import { Seo } from "@/components/Seo";

export default function PaymentSuccess() {
    const { lang, t } = useLang();
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [data, setData] = useState(null);
    const [email, setEmail] = useState("");
    const tries = useRef(0);

    useEffect(() => {
        if (!sessionId) return undefined;
        const poll = async () => {
            try {
                const { data: d } = await api.get(`/payments/status/${sessionId}`);
                setData(d);
                if (d.payment_status !== "paid" && tries.current < 20) {
                    tries.current += 1;
                    setTimeout(poll, 2500);
                }
            } catch {
                if (tries.current < 20) { tries.current += 1; setTimeout(poll, 2500); }
            }
        };
        poll();
        return () => { tries.current = 99; };
    }, [sessionId]);

    const paid = data?.payment_status === "paid";
    const booking = data?.booking;

    const downloadVoucher = async () => {
        try {
            const resp = await api.get(`/public/bookings/${booking.code}/voucher`, { params: { email }, responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `voucher-${booking.code}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error(t.booking.errorGeneric);
        }
    };

    return (
        <div data-testid="payment-success-page" className="marble-bg min-h-screen pt-40 pb-24">
            <Seo lang={lang} title={`${t.payment.successTitle} — TOURVOYAGERS`} />
            <div className="max-w-xl mx-auto px-5 text-center">
                {paid ? (
                    <>
                        <CheckCircle2 className="mx-auto text-amber-400" size={60} data-testid="payment-confirmed-icon" />
                        <h1 className="mt-6 font-display text-4xl text-amber-50">{t.payment.successTitle}</h1>
                        <p className="mt-4 text-sm text-stone-300 leading-relaxed">{t.payment.confirmedMsg}</p>
                        {booking && (
                            <div className="mt-8 gold-card rounded-xl p-8">
                                <p className="text-[10px] uppercase tracking-[0.3em] text-amber-500/80">{t.payment.bookingCode}</p>
                                <p className="mt-1 font-mono-accent text-2xl text-amber-300" data-testid="payment-booking-code">{booking.code}</p>
                                <p className="mt-2 text-xs text-stone-400">{t.payment.statusLabel}: <span className="text-amber-200">{t.status[booking.status] || booking.status}</span></p>
                                {booking.package_name && <p className="mt-1 text-xs text-stone-500">{pick(booking.package_name, lang)}</p>}
                                {booking.status === "confirmed" && (
                                    <div className="mt-6 border-t border-amber-500/15 pt-5">
                                        <label className="block text-[11px] uppercase tracking-widest text-amber-500/80">{t.payment.emailForVoucher}</label>
                                        <input data-testid="voucher-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-lux mt-2" />
                                        <button data-testid="voucher-download-button" onClick={downloadVoucher} disabled={!email.includes("@")} className="mt-3 w-full rounded-full bg-amber-400 px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] text-black transition-all hover:bg-amber-300 disabled:opacity-40">
                                            {t.payment.downloadVoucher}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <Loader2 className="mx-auto animate-spin text-amber-400" size={52} data-testid="payment-checking-spinner" />
                        <h1 className="mt-6 font-display text-3xl text-amber-50">{t.payment.checking}</h1>
                        <p className="mt-4 text-sm text-stone-400">{t.payment.pendingMsg}</p>
                    </>
                )}
                <Link to="/" data-testid="payment-back-home" className="mt-10 inline-block text-xs uppercase tracking-[0.18em] text-amber-300 underline underline-offset-4">{t.payment.backHome}</Link>
            </div>
        </div>
    );
}
